import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Icon, usePageMeta, Eyebrow, FinalCTA } from "../components/Shared";
import { SERVICES, WHY, STEPS, INDUSTRIES, IMAGES, COMPANY } from "../data/site";

export default function Home() {
  usePageMeta(
    "Cross-Docking & Freight Solutions in the GTA",
    "CoreDock Logistics provides professional cross-docking, short-term warehousing, freight handling and LTL pickup-and-delivery from our Mississauga facility near Highway 401 and Toronto Pearson Airport."
  );

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] bg-navy text-white overflow-hidden flex items-center" data-testid="hero">
        <div className="absolute inset-0">
          <img src={IMAGES.heroTruck} alt="Freight truck on the highway" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/70 to-navy/95" />
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <Reveal><Eyebrow>{COMPANY.tagline}</Eyebrow></Reveal>

          <Reveal delay={0.1}>
            <h1 className="font-display font-bold tracking-tight text-[2.6rem] sm:text-5xl lg:text-7xl leading-[1.02] mt-5 max-w-5xl">
              Fast, Reliable <span className="text-orange">Cross-Docking</span> &amp; Freight Solutions in the GTA
            </h1>
          </Reveal>

          <Reveal delay={0.2} className="mt-7 max-w-2xl">
            <p className="text-lg text-white/75 leading-relaxed">
              Professional cross-docking, short-term warehousing, freight handling, and LTL pickup-and-delivery
              from our Mississauga facility.
            </p>
          </Reveal>

          <Reveal delay={0.3} className="mt-9 flex flex-col sm:flex-row gap-4">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="hero-call-btn"
               className="btn-orange px-8 py-4 font-semibold uppercase tracking-wide flex items-center justify-center gap-2">
              <Phone size={18} /> Call 647-204-6323
            </a>
            <Link to="/quote" data-testid="hero-quote-btn"
                  className="px-8 py-4 font-semibold uppercase tracking-wide border-2 border-white/40 hover:border-orange hover:text-orange transition-colors flex items-center justify-center gap-2">
              Request a Quote <ArrowRight size={18} />
            </Link>
          </Reveal>

          <Reveal delay={0.4} className="mt-10 flex items-center gap-3 text-sm text-white/55">
            <span className="w-8 h-px bg-orange" />
            Strategically located near Highway 401 and Toronto Pearson Airport
          </Reveal>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 md:py-28 max-w-[1440px] mx-auto px-6 md:px-12" data-testid="services-overview">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>What We Do</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-3 max-w-2xl">
              Freight handling built for speed and security
            </h2>
          </div>
          <Link to="/services" className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2 text-navy hover:text-orange transition-colors shrink-0">
            All Services <ArrowUpRight size={18} />
          </Link>
        </div>

        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {SERVICES.slice(0, 6).map((s) => (
            <StaggerItem key={s.slug}>
              <Link to="/services" data-testid={`service-card-${s.slug}`}
                    className="group bg-white p-8 h-full flex flex-col hover:bg-navy transition-colors duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-navy text-orange group-hover:bg-orange group-hover:text-white transition-colors">
                  <Icon name={s.icon} size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-display font-bold text-xl mt-6 group-hover:text-white transition-colors">{s.title}</h3>
                <p className="mt-3 text-sm text-text-secondary leading-relaxed group-hover:text-white/60 transition-colors flex-1">{s.short}</p>
                <span className="mt-5 text-sm font-semibold uppercase tracking-wide text-orange flex items-center gap-1">
                  Learn More <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-navy text-white py-20 md:py-28" data-testid="why-choose">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl mb-14">
            <Eyebrow>Why CoreDock</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-3">
              A responsive, safety-focused freight partner in Mississauga
            </h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {WHY.map((w, i) => (
              <StaggerItem key={w.title} className="border-t border-white/15 pt-6">
                <span className="font-mono text-orange text-sm">{String(i + 1).padStart(2, "0")}</span>
                <Icon name={w.icon} size={26} strokeWidth={1.5} className="text-white mt-4" />
                <h3 className="font-display font-bold text-lg mt-4">{w.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{w.desc}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 max-w-[1440px] mx-auto px-6 md:px-12" data-testid="how-it-works">
        <Reveal className="mb-12">
          <Eyebrow>How It Works</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-3 max-w-2xl">
            Four steps from request to delivery
          </h2>
        </Reveal>
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((s) => (
            <StaggerItem key={s.n}>
              <span className="font-display font-bold text-5xl text-orange/25">{s.n}</span>
              <div className="w-10 h-1 bg-orange mt-2 mb-4" />
              <h3 className="font-display font-bold text-xl">{s.title}</h3>
              <p className="mt-2 text-sm text-text-secondary leading-relaxed">{s.desc}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-white border-y border-border py-20 md:py-28" data-testid="industries-overview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            <Reveal className="lg:sticky lg:top-28 self-start">
              <Eyebrow>Who We Serve</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-3">
                Industries that move through the GTA
              </h2>
              <p className="mt-5 text-text-secondary leading-relaxed">
                We help carriers, brokers, manufacturers, distributors, importers, and retailers move
                freight efficiently across the GTA and Ontario.
              </p>
              <Link to="/industries" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-navy hover:text-orange transition-colors">
                Explore Industries <ArrowUpRight size={18} />
              </Link>
            </Reveal>
            <Stagger className="lg:col-span-2 grid sm:grid-cols-2 gap-px bg-border border border-border">
              {INDUSTRIES.map((ind) => (
                <StaggerItem key={ind.title} className="bg-offwhite p-8 hover:bg-white transition-colors">
                  <Icon name={ind.icon} size={26} strokeWidth={1.5} className="text-orange" />
                  <h3 className="font-display font-bold text-lg mt-4">{ind.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary leading-relaxed">{ind.desc}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
